<template>
  <div class="after-sign-container">
    <transition mode="out-in" name="fade">
      <AfterSignOptions
        v-if="currentStep === AfterSignStep.Init"
        id="init"
        key="1"
        @select="handleSelect"
      />
      <AfterSignCreateSignup
        v-else-if="currentStep === AfterSignStep.Signup"
        id="signup"
        key="2"
        :recipient="recipient"
        :error="afterSign.error"
        :did-you-mean="afterSign.didYouMean"
        @email="handleEmail"
      />
      <AfterSignCreateSignupComplete
        v-else-if="currentStep === AfterSignStep.SignupComplete"
        id="signup-complete"
        key="3"
        :redirect-url="afterSign.redirectUrl"
      />
      <AfterSignFeedback
        v-else-if="currentStep === AfterSignStep.Feedback"
        id="feedback"
        key="4"
        @feedback="handleFeedback"
      />
      <AfterSignFeedbackComplete
        v-else-if="currentStep === AfterSignStep.FeedbackComplete"
        id="feedback-complete"
        key="5"
      />
    </transition>
    <div class="footer">
      <span class="powered-logo">
        <PoweredByLogo />
      </span>
      <div v-if="showActionButtons" class="action-buttons">
        <FriedButton
          v-if="showCancelButton"
          class="cancel-button"
          data-external="after-sign-modal-cancel-btn"
          :button-type="ButtonType.Secondary"
          @click="handleClose"
          >{{ $t('cancel') }}
        </FriedButton>
        <FriedButton
          v-show="currentStep === AfterSignStep.Signup"
          class="signup-button"
          data-external="after-sign-modal-create-free-acc-btn"
          :disabled="!validEmail"
          :loading="afterSign.loading"
          @click="handleSignup"
          >{{ $t('create-a-free-account') }}
        </FriedButton>
        <FriedButton
          v-if="currentStep === AfterSignStep.Feedback"
          class="feedback-button"
          data-external="after-sign-feedback-button"
          :button-type="ButtonType.Secondary"
          @click="handleSendFeedback"
          >{{ $t('submit-feedback') }}
        </FriedButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ButtonType } from '@getaccept/fried-tofu';
import { sanitizeEmail, validateEmail } from '@getaccept/lib-shared-new/src/helpers';
import type { SigningPageRecipient } from '@getaccept/lib-shared-new/src/types/signing-page-recipient';
import type { PropType } from 'vue';
import { onMounted, computed, ref, defineComponent } from 'vue';
import { AfterSignStep } from '../../../types/enums/after-sign-step';
import type { AfterSign as IAfterSign } from '../../../types/after-sign';
import PoweredByLogo from '../../../assets/powered-by-ga.svg';
import AfterSignCreateSignup from './AfterSignCreateSignup.vue';
import AfterSignFeedback from './AfterSignFeedback.vue';
import AfterSignOptions from './AfterSignOptions.vue';
import AfterSignFeedbackComplete from './AfterSignFeedbackComplete.vue';
import AfterSignCreateSignupComplete from './AfterSignCreateSignupComplete.vue';

export default defineComponent({
  components: {
    AfterSignCreateSignup,
    AfterSignFeedback,
    AfterSignOptions,
    AfterSignFeedbackComplete,
    AfterSignCreateSignupComplete,
    PoweredByLogo,
  },
  props: {
    recipient: {
      type: Object as PropType<SigningPageRecipient>,
      required: true,
    },
    afterSign: {
      type: Object as PropType<IAfterSign>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const emailInput = ref('');
    const feedbackInput = ref('');

    const showActionButtons = computed(
      (): boolean =>
        currentStep.value !== AfterSignStep.Init &&
        currentStep.value !== AfterSignStep.FeedbackComplete
    );

    const validEmail = computed(
      (): boolean => emailInput.value.length > 0 && validateEmail(emailInput.value.toLowerCase())
    );

    const recipientEmail = computed((): string => props.recipient.email?.toLowerCase() || '');

    const showCancelButton = computed(
      (): boolean =>
        currentStep.value !== AfterSignStep.SignupComplete &&
        currentStep.value !== AfterSignStep.FeedbackComplete
    );

    const currentStep = computed((): AfterSignStep => props.afterSign.step);

    const cleanEmail = computed((): string => sanitizeEmail(emailInput.value));

    const handleChangeStep = (step: AfterSignStep) => {
      emit('changeStep', step);
    };

    const handleSelect = (option: AfterSignStep) => {
      handleChangeStep(option);
      emit('response', option);
    };

    const handleEmail = (email: string) => {
      emailInput.value = sanitizeEmail(email);
    };

    const handleFeedback = (feedback: string) => {
      feedbackInput.value = feedback;
    };

    const handleSendFeedback = () => {
      emit('feedback', feedbackInput.value, cleanEmail.value);
      handleChangeStep(AfterSignStep.FeedbackComplete);
    };

    const handleSignup = () => {
      emit('signup', cleanEmail.value);
    };

    const handleClose = () => {
      emit('close');
    };

    onMounted(() => {
      emailInput.value = recipientEmail.value;
    });

    return {
      AfterSignStep,
      ButtonType,
      emailInput,
      feedbackInput,
      showActionButtons,
      validEmail,
      recipientEmail,
      showCancelButton,
      currentStep,
      cleanEmail,
      handleChangeStep,
      handleSelect,
      handleEmail,
      handleFeedback,
      handleSendFeedback,
      handleSignup,
      handleClose,
    };
  },
});
</script>
<style lang="scss" scoped>
.after-sign-container {
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: var(--spacing-150);
  justify-content: space-between;
  padding-top: 0;

  @media screen and (max-width: $sm) {
    min-height: auto;
    width: 100%;
  }

  .footer {
    width: 100%;

    .powered-logo {
      display: flex;
      justify-content: center;
      margin-bottom: var(--spacing-100);
      padding: var(--spacing-100);

      @media screen and (max-width: $sm) {
        margin-bottom: 0;
      }
    }

    .action-buttons {
      display: flex;
      width: 100%;
      justify-content: flex-end;

      @media screen and (max-width: $sm) {
        flex-direction: column-reverse;
      }

      .cancel-button {
        margin-right: var(--spacing-50);

        @media screen and (max-width: $sm) {
          margin-right: 0;
          margin-top: var(--spacing-50);
        }
      }
    }
  }
}
</style>
